import { PartConditionOptions } from '../@types/part.type';
import { ShipmentStatusCodes } from '../@types/shipment.type';
import { DropdownItem } from '../components/atoms/SelectMenu';
import { ColumnSelectionItem } from '../components/templates/ColumnSelection';
import { CustomShippingPackageType } from '../configs/enums';

// Statuses a shipment must be in to be eligible for individual receiving.
// Shared between the Individual Shipment Receive page itself and any other
// entry point into that flow (e.g. the Dispatch detail page's "Receive"
// button) so both stay in sync on what "receivable" means.
export const INCOMING_STATUSES = [
  ShipmentStatusCodes.InTransit,
  ShipmentStatusCodes.Partial,
  ShipmentStatusCodes.Pending,
];

// Single source of truth for "is this status receivable" — both Receive entry
// points (ShipmentDetailHeader's button, ShipmentList's row action) call this
// instead of each re-implementing the INCOMING_STATUSES.some(...) check, so a
// future change to what counts as receivable can't leave them disagreeing.
export const isReceivableStatus = (statusCode?: string): boolean =>
  INCOMING_STATUSES.some((s) => s === statusCode);

export const SAMPLE_ADD_STOCK_DETAIL = {
  id: 0,
  source: '',
  sourceNodeId: 0,
  destination: '',
  destinationNodeId: 0,
  shipmentStatusCode: 'pending',
  clientHierarchy: '',
  clientHierarchySearch: '',
  clientHierarchyName: '',
  client: '',
  contract: ['any'],
  customerId: '',
  connoteNo: '',
  expectedArrivalDate: '',
  shippingCost: NaN,
  referenceNumber1: '',
  referenceNumber2: '',
  referenceNumber3: '',
  note: '',
  shipmentTypeCode: 'add-stock',
};

export const ADD_STOCK_DETAILS_HELPER_TEXT = {
  sourceHelperText: '',
  destinationHelperText: '',
  clientContractHelperText: '',
};

export const SAMPLE_DISPATCH_DETAIL = {
  source: '',
  sourceNodeId: 0,
  destination: '',
  destinationNodeId: 0,
  shipmentStatusCode: 'in-transit',
  clientHierarchy: '',
  clientHierarchyName: '',
  client: '',
  contract: [],
  serviceId: 0,
  bypassReceipt: false,
  overrideConsumption: false,
  connoteNo: '',
  note: '',
  shippingCost: 0,
  shipmentTypeCode: 'dispatch',
  referenceNumber1: '',
  referenceNumber2: '',
  referenceNumber3: '',
};

export const DISPATCH_DETAILS_HELPER_TEXT = {
  sourceHelperText: '',
  destinationHelperText: '',
  clientContractHelperText: '',
};

export const SFH_OPTIONS = {
  softwares: [],
  firmwares: [],
  hardwares: [],
};

export const SAMPLE_MANIFEST_SERIAL_DETAIL = {
  id: 0,
  partTypeId: 0,
  sfhOptions: SFH_OPTIONS,
  fleetTagOptions: [],
  conditionOptions: [],
  softwareVersion: '',
  firmwareVersion: '',
  hardwareRevision: '',
  fleetTag: '',
  condition: PartConditionOptions.GOOD,
  smartInput: '',
  serialsDataSet: [],
};

export const SAMPLE_NON_SERIALISED_ATTRIBUTES = {
  condition: 'good',
  fleetTag: '',
};

export const SAMPLE_MANIFEST_ATTRIBUTE_DETAIL = {
  partTypeId: 0,
  partTypeName: '',
  partTypeNo: '',
  attributes: SAMPLE_NON_SERIALISED_ATTRIBUTES,
  serials: SAMPLE_MANIFEST_SERIAL_DETAIL,
};

export const SAMPLE_DISPATCH_MANIFEST_DETAIL = {
  serialisedManifestData: [
    {
      partTypeId: 2,
      partTypeName: 'Aldi Key board',
      partTypeNo: '478',
      quantity: 1,
      items: [
        {
          selected: true,
          condition: 'Good',
          serial: 'X12345',
          softwareVersion: 'V1.2',
          hardwareVersion: 'V1.4',
          firmwareVersion: 'V1.9',
          fleetTag: 'Fleet Tag 2',
          location: 'Melbourne',
          locationId: 120,
        },
        {
          selected: false,
          condition: 'Good',
          serial: 'X12346',
          softwareVersion: 'V1.0',
          hardwareVersion: 'V1.4',
          firmwareVersion: 'V4.9',
          fleetTag: 'Fleet Tag 2',
          location: 'Melbourne',
          locationId: 120,
        },
      ],
      serialisation: 'Serialised',
    },
  ],
  nonSerializedManifestData: [
    {
      partTypeId: 1,
      partTypeName: 'USB Cable Connectors',
      partTypeNo: '224',
      quantity: 20,
      items: [
        {
          selected: true,
          condition: 'Good',
          fleetTag: 'Fleet Tag 1',
          location: 'Melbourne',
          locationId: 120,
        },
      ],
      serialisation: 'Consumable',
    },
  ],
};

export const INITIAL_DISPATCH_MANIFEST = {
  serialisedManifestData: [],
  nonSerializedManifestData: [],
};

export const MANUAL_SHIPPING_DATA = {
  connoteNo: '',
  shippingProviderId: 0,
};

export const INTEGRATED_FREIGHT_SHIPPING_DATA = {
  shippingProviderId: 0,
  serviceType: '',
  selectedPackages: [],
  deliveryNote: '',
  isSafeDrop: false,
  isAuthToLeave: false,
  isCustomAddress: false,
};

export const CUSTOM_PACKAGE_TYPES: DropdownItem[] = [
  {
    value: CustomShippingPackageType.Carton,
    label: CustomShippingPackageType.Carton,
  },
  {
    value: CustomShippingPackageType.Pallet,
    label: CustomShippingPackageType.Pallet,
  },
  {
    value: CustomShippingPackageType.Satchel,
    label: CustomShippingPackageType.Satchel,
  },
];

export const SAMPLE_SHIPMENT_BATCH_RECEIVAL = {
  partTypeId: 0,
  partTypeName: '',
  isPartTypePopup: false,
  putAwayLocationId: 0,
  putAwayLocationName: '',
  isPartTypeLiveSearch: false,
  isOther: false,
  smartInput: '',
  nonSerialOptions: [],
  nonSerializedShipmentManifestItem: [],
  serializedShipmentManifestItem: [],
  selectedSerializedShipmentManifestItem: [],
  nonSerializedShipmentManifestItemCount: 0,
  serializedShipmentManifestItemCount: 0,
};

export const SAMPLE_SHIPMENT_INDIVIDUAL_RECEIVAL = {
  nonSerializedShipmentManifestItem: [],
  selectedNonSerializedShipmentManifestItem: [],
  serializedShipmentManifestItem: [],
  selectedSerializedShipmentManifestItem: [],
};

export const SAMPLE_SERIAL_POPUP = {
  id: 0,
  partTypeId: 0,
  sfhOptions: SFH_OPTIONS,
  fleetTagOptions: [],
  conditionOptions: [],
};

export const batchRecieveColumnSelectionItems: Array<ColumnSelectionItem> = [
  {
    title: 'Shipment',
    value: [
      {
        title: 'ID',
        field: 'id',
        value: true,
      },
      {
        title: 'Created By',
        field: 'createdByName',
        value: true,
      },
      {
        title: 'Created At',
        field: 'createdAt',
        value: true,
      },
      {
        title: 'Destination',
        field: 'destinationNodeName',
        value: true,
      },
      {
        title: 'Total Qty',
        field: 'qty',
        value: true,
      },
    ],
  },
];

export const shipmentListColumnSelectionItems: Array<ColumnSelectionItem> = [
  {
    title: 'Shipment',
    value: [
      {
        title: 'Shipment ID',
        field: 'id',
        value: true,
      },
      {
        title: 'Type',
        field: 'shipmentTypeCode',
        value: true,
      },
      {
        title: 'Status',
        field: 'shipmentStatusCode',
        value: true,
      },
      {
        title: 'Created At',
        field: 'createdAt',
        value: true,
      },
      {
        title: 'Created By',
        field: 'createdByName',
        value: true,
      },
      {
        title: 'Updated At',
        field: 'updatedAt',
        value: true,
      },
      {
        title: 'Updated By',
        field: 'updatedByName',
        value: true,
      },
      {
        title: 'Completed At',
        field: 'completedAt',
        value: true,
      },
      {
        title: 'Source',
        field: 'sourceName',
        value: true,
      },
      {
        title: 'Source Node Type',
        field: 'sourceNodeType',
        value: false,
      },
      {
        title: 'Destination',
        field: 'destinationName',
        value: true,
      },
      {
        title: 'Destination Node Type',
        field: 'destinationNodeType',
        value: false,
      },
      {
        title: 'Client/Contract',
        field: 'clientContractNames',
        value: true,
      },
      {
        title: 'Notes',
        field: 'notes',
        value: true,
      },
    ],
  },
  {
    title: 'Reference #',
    value: [
      {
        title: 'Reference 1',
        field: 'referenceNumber1',
        value: true,
      },
      {
        title: 'Reference 2',
        field: 'referenceNumber2',
        value: true,
      },
      {
        title: 'Reference 3',
        field: 'referenceNumber3',
        value: true,
      },
    ],
  },
  {
    title: 'Totals',
    value: [
      {
        title: 'Quantity Total',
        field: 'qtyTotal',
        value: true,
      },
      {
        title: 'Quantity Received',
        field: 'qtyReceived',
        value: true,
      },
      {
        title: 'Quantity Pending',
        field: 'qtyPending',
        value: true,
      },
    ],
  },
  {
    title: 'Freight',
    value: [
      {
        title: 'Courier Name',
        field: 'courierName',
        value: true,
      },
      {
        title: 'Connote No.',
        field: 'connoteNo',
        value: true,
      },
      {
        title: 'Latest Tracking Event',
        field: 'latestTrackingStatus',
        value: true,
      },
    ],
  },
  {
    title: 'Attributes',
    value: [
      {
        title: 'Expected Arrival Date',
        field: 'arrivalDate',
        value: true,
      },
      {
        title: 'Shipping Cost',
        field: 'shippingCost',
        value: true,
      },
    ],
  },
  {
    title: 'Labels',
    value: [
      {
        title: 'Print Label',
        field: 'printLabel',
        value: true,
      },
    ],
  },
];

export const AUSPOST_STARTRACK_COURIER_NAME = 'Auspost/StarTrack';

// Preferred dispatch order for non-serialised stock conditions — used to rank
// NSPart rows so the best condition is offered first when building a manifest.
// Entries are condition display NAMES (not condition codes); names missing
// from the list (e.g. Reserved) sort after all listed ones.
export const CONDITION_ORDER = [
  'Good',
  'Staging',
  'Quarantine',
  'Faulty',
  'Repair',
  'DOA',
  'BER',
  'Destroyed',
  'Missing',
  'In Transit',
  'Installed',
];
